import { groupBy, map } from "lodash";
import Lottie from "lottie-react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import * as fileloading from "../animations/file-folder.json";
import {
  getCareTeam,
  getDischargedClientsForProgramUnit,
  getDivisionsProgramUnits,
  getFacilityAddresses,
  getFormsySubmissionsForMultiple,
  getMyCaseload,
  getProgramsByState,
  getTeamCarePlans,
  getTeamCaseload,
  getUnsignedProgramEncounters,
  getUsers,
  searchContact,
} from "../components/services/soar";
import {
  checkKeyPress,
  getFieldDate,
  getFieldName,
  getFieldText,
} from "./../scripts/helperScripts";
import AllCaseloadClientSearch from "./AllCaseloadClientSearch";
import ClientCard from "./common/ClientCard";
import ClientMiniCard from "./common/ClientMiniCard";
import SearchBox from "./common/SearchBox";
import Select from "./common/Select";
import ToggleSwitch from "./common/ToggleSwitch";
import FamilyChartModal from "./common/FamilyChartModal";

function AllCaseloads(props) {
  const location = useLocation();
  const [data, setData] = useState({
    CaseloadType: "Program Unit",
    Provider: "",
    ProviderSearch: "",
    ShowDischarged: false,
    SelectedProgram:
      location.state?.selectedProgram ||
      localStorage.getItem("selectedTeam") ||
      "",
    SearchQuery: "",
    SortType: "A - Z",
  });
  const [loadStatus, setLoadStatus] = useState({});
  const [providers, setProviders] = useState([]);
  const isMountedRef = useRef(true);
  const [caseload, setCaseload] = useState([]);
  const [providerCaseload, setProviderCaseload] = useState([]);
  const [activeCaseload, setActiveCaseload] = useState([]);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [programs, setPrograms] = useState();
  const [careplans, setCarePlans] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [formHistory, setFormHistory] = useState([]);
  //const [dischargedCaseload, setDischargedCaseload] = useState([]); // TODO: upon loading discharged clients, store here. only fetch discharged clients if the length = 0. clear this out on selected program change.
  const [stateDivisions, setStateDivisions] = useState();
  const [familyChart, setFamilyChart] = useState();
  const [controllerSignal, setControllerSignal] = useState();
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Route Access Protection
  useEffect(() => {
    if (!safyInfo?.user?.modules || !safyInfo.user.divisions) {
      navigate("/");
    }

    if (
      safyInfo.user.modules.includes("Team Caseload") !== true &&
      safyInfo.user.modules.includes("Admin") !== true
    ) {
      navigate("/");
    }
  });

  const safyUser = useMemo(() => {
    return safyInfo?.user;
  }, [safyInfo]);

  const userDivisions = useMemo(() => {
    return safyUser?.divisions;
  }, [safyUser]);

  const userHasElevatedPrivileges = useMemo(() => {
    return (
      safyUser?.modules?.includes("Admin") ||
      safyUser?.modules?.includes("Rev Cycle") ||
      safyUser?.safyInfo?.isExecutiveOnCall === "1"
    );
  }, [safyUser]);

  const loadingInProgress = useMemo(() => {
    return (
      loadStatus.loadingInitialRecords ||
      loadStatus.loadingInitialCaseload ||
      loadStatus.loadingCaseload ||
      loadStatus.loadingContacts
    );
  }, [
    loadStatus.loadingInitialRecords,
    loadStatus.loadingInitialCaseload,
    loadStatus.loadingCaseload,
    loadStatus.loadingContacts,
  ]);

  useEffect(() => {
    document.title = "SAFY Portal - Team Caseload";
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => {
      document.title = "SAFY Portal";
      isMountedRef.current = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const loadFacilities = async () => {
      const response = await getFacilityAddresses("ccplive");

      if (!isMountedRef.current || !response) {
        return;
      }

      const allDivisions = {};

      response.forEach((element) => {
        const state = getFieldName(element.owningbusinessunit);
        const division = getFieldName(element.ccx_parentid);

        if (allDivisions[state]) {
          allDivisions[state].push(division);
        } else {
          allDivisions[state] = [division];
        }
      });

      setStateDivisions(allDivisions);
    };

    loadFacilities();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      const response = await getUsers();

      if (isMountedRef.current && response) {
        const options = response.map((user) => ({
          name: `${user.firstname} ${user.lastname}`,
          _id: `${user.internalemailaddress}`,
        }));
        setProviders(options);
      }
    };

    loadUsers();
  }, []);

  const loadSubmissionsForCaseload = async (currentCaseload) => {
    if (currentCaseload.length > 0) {
      console.log("Loading Clients and Compliancy");
      const submissionResponse = await getFormsySubmissionsForMultiple(
        currentCaseload.map((c) => c.ccx_patient["#text"])
      );

      if (submissionResponse && isMountedRef.current) {
        setFormHistory(submissionResponse);
        console.log("Compliancy Loaded");
      }
    }
  };

  useEffect(() => {
    if (
      userDivisions?.length > 0 &&
      !loadStatus.loadingInitialCaseload &&
      !loadStatus.initialCaseloadLoaded
    ) {
      if (userHasElevatedPrivileges) {
        setData((d) => {
          return { ...d, SelectedProgram: "" };
        });
        setLoadStatus((ls) => {
          return { ...ls, initialCaseloadLoaded: true };
        });
      } else {
        const loadCaseload = async () => {
          setLoadStatus((ls) => {
            return { ...ls, loadingInitialCaseload: true };
          });

          const caseloadResponse = await Promise.all([
            getTeamCaseload(userDivisions),
            getTeamCarePlans(userDivisions),
          ]);

          if (!isMountedRef.current) {
            return;
          }

          const teamCaseloadResponse = caseloadResponse[0];
          const carePlansResponse = caseloadResponse[1];

          if (teamCaseloadResponse) {
            setActiveCaseload(teamCaseloadResponse);

            if (
              location.state?.selectedProgram ||
              localStorage.getItem("selectedTeam")
            ) {
              setCaseload(teamCaseloadResponse);
              await loadSubmissionsForCaseload(teamCaseloadResponse);
            }
          }

          setCarePlans(carePlansResponse || []);
          setLoadStatus((ls) => {
            return {
              ...ls,
              loadingInitialCaseload: false,
              initialCaseloadLoaded: true,
            };
          });
        };

        loadCaseload();
      }
    }
  }, [
    location.state?.selectedProgram,
    userHasElevatedPrivileges,
    userDivisions,
    loadStatus.loadingInitialCaseload,
    loadStatus.initialCaseloadLoaded,
  ]);

  const loadCaseloadForSelectedProgram = useCallback(
    async (selectedProgram, showDischarged, programUnits) => {
      setLoadStatus((ls) => {
        return { ...ls, loadingCaseload: true };
      });
      if (selectedProgram.includes("SAFY of ")) {
        const response = await getProgramsByState(
          selectedProgram.split(" of ")[1],
          showDischarged
        );

        if (!isMountedRef.current) {
          return;
        }

        if (response) {
          setCaseload(response);

          if (userHasElevatedPrivileges) {
            await loadSubmissionsForCaseload(response);
          }
        }
        setLoadStatus((ls) => {
          return { ...ls, loadingCaseload: false };
        });
      } else if (showDischarged) {
        // get discharged clients/client programs for program unit

        const response = await getDischargedClientsForProgramUnit(
          selectedProgram
        );

        if (!isMountedRef.current) {
          return;
        }

        if (response) {
          setCaseload(response);

          if (userHasElevatedPrivileges) {
            await loadSubmissionsForCaseload(response);
          }
        }

        setLoadStatus((ls) => {
          return { ...ls, loadingCaseload: false };
        });
      } else {
        if (userHasElevatedPrivileges) {
          const selectedProgramOption = (programUnits || programs).filter(
            (option) => option._id === selectedProgram
          )[0];

          if (selectedProgramOption) {
            let divisions = [selectedProgramOption.name.split(" - ")[0]];
            setLoadStatus((ls) => {
              return { ...ls, loadingCaseload: true };
            });
            const response = await Promise.all([
              getTeamCaseload(divisions),
              getTeamCarePlans(divisions),
            ]);

            if (!isMountedRef.current) {
              return;
            }

            const teamCaseloadResponse = response[0];
            const carePlansResponse = response[1];

            if (teamCaseloadResponse) {
              setCaseload(teamCaseloadResponse);
              setActiveCaseload(teamCaseloadResponse);
              await loadSubmissionsForCaseload(teamCaseloadResponse);
            }

            setCarePlans(carePlansResponse || []);
          }
        } else {
          setCaseload([...activeCaseload]); // no special handling of a type of Selected Program/User Roles, caseload filtered to Selected Program elsewhere
        }
      }

      setLoadStatus((ls) => {
        return { ...ls, loadingCaseload: false };
      });
    },
    [activeCaseload, programs, userHasElevatedPrivileges]
  );

  useEffect(() => {
    if (
      userDivisions?.length > 0 &&
      !loadStatus.loadingInitialRecords &&
      !loadStatus.initialRecordsLoaded
    ) {
      const loadProgramUnits = async () => {
        setLoadStatus((ls) => {
          return { ...ls, loadingInitialRecords: true };
        });
        const response = await getDivisionsProgramUnits(userDivisions);

        if (!isMountedRef.current || !response) {
          return;
        }

        const programUnits = response
          .filter((unit) => !unit.ccx_name.includes("Foster Homes"))
          .map((unit) => {
            return {
              _id: unit.ccx_unitid,
              name: unit.ccx_name,
            };
          })
          .sort(sortObjectName);

        if (userHasElevatedPrivileges) {
          programUnits.splice(
            0,
            0,
            {
              _id: "SAFY of Alabama",
              name: "SAFY of Alabama",
            },
            {
              _id: "SAFY of Colorado",
              name: "SAFY of Colorado",
            },
            {
              _id: "SAFY of Indiana",
              name: "SAFY of Indiana",
            },
            {
              _id: "SAFY of Kentucky",
              name: "SAFY of Kentucky",
            },
            {
              _id: "SAFY of Nevada",
              name: "SAFY of Nevada",
            },
            {
              _id: "SAFY of Ohio",
              name: "SAFY of Ohio",
            },
            {
              _id: "SAFY of South Carolina",
              name: "SAFY of South Carolina",
            }
          );
        } else {
          if (
            userDivisions.includes("Boulder") ||
            userDivisions.includes("Fort Collins") ||
            userDivisions.includes("Aurora")
          ) {
            programUnits.splice(0, 0, {
              _id: "SAFY of Colorado",
              name: "SAFY of Colorado",
            });
          }
        }

        setPrograms(programUnits);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingInitialRecords: false,
            initialRecordsLoaded: true,
          };
        });
      };

      loadProgramUnits();
    }
  }, [
    userHasElevatedPrivileges,
    userDivisions,
    loadStatus.loadingInitialRecords,
    loadStatus.initialRecordsLoaded,
  ]);

  const sortObjectName = (a, b) => {
    const objectA = a.name.toUpperCase();
    const objectB = b.name.toUpperCase();

    let comparison = 0;
    if (objectA > objectB) {
      comparison = 1;
    } else if (objectA < objectB) {
      comparison = -1;
    }
    return comparison;
  };

  const handleSelectedProgramChange = async ({ currentTarget: input }) => {
    localStorage.setItem("selectedTeam", input.value);
    handleChange({ currentTarget: input });
    let showDischarged = data.ShowDischarged;

    if (!input.value) {
      setCaseload([...activeCaseload]);
      setData((d) => {
        return { ...d, ShowDischarged: false };
      });
      showDischarged = false;
      return;
    }

    loadCaseloadForSelectedProgram(input.value, showDischarged);

    setLoadStatus((ls) => {
      return { ...ls, encountersLoaded: false };
    });
    const response = await getUnsignedProgramEncounters(input.value);

    if (!isMountedRef.current) {
      return;
    }

    if (response) {
      setEncounters(
        response.filter((enc) => enc.ccx_draftsigned["@name"] === "No")
      );
    }

    setLoadStatus((ls) => {
      return { ...ls, encountersLoaded: true };
    });
  };

  const handleProviderChange = async ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    setLoadStatus((ls) => {
      return { ...ls, loadingCaseload: true };
    });
    const response = await getMyCaseload(input.value);

    if (!isMountedRef.current) {
      return;
    }

    if (response) {
      setProviderCaseload(response);
    }

    setLoadStatus((ls) => {
      return { ...ls, loadingCaseload: false };
    });
  };

  const handleShowDischargeChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });
    loadCaseloadForSelectedProgram(data.SelectedProgram, input.checked);
  };

  const handleSearchQueryChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (caseload.length === 0) {
      setCaseload(activeCaseload);
    }
  };

  const handleChange = ({ currentTarget: input }) => {
    setData((d) => {
      const obj = { ...d };
      obj[input.name] = input.type === "checkbox" ? input.checked : input.value;
      return obj;
    });
  };

  const filteredCaseload = useMemo(() => {
    if (caseload.length === 0) {
      return [];
    }

    let filtered = caseload;

    if (data.SearchQuery) {
      filtered = filtered.filter(
        (program) =>
          program["con.fullname"]
            .toLowerCase()
            .includes(data.SearchQuery.toLowerCase()) ||
          (program["con.ccx_mpistr"] !== undefined &&
            program["con.ccx_mpistr"]
              .toLowerCase()
              .includes(data.SearchQuery.toLowerCase()))
      );
    }

    if (data.SelectedProgram) {
      filtered = filtered.filter(
        (program) =>
          getFieldText(program.ccx_programunit) === data.SelectedProgram ||
          getFieldName(program.ccx_company).includes(data.SelectedProgram)
      );
    }

    filtered.sort((a, b) => {
      if (data.SortType === "A - Z") {
        return a["con.fullname"].localeCompare(b["con.fullname"]);
      }

      let aDate, bDate;

      if (data.SortType === "Admission") {
        aDate = moment(getFieldDate(a.ccx_admitdate));
        bDate = moment(getFieldDate(b.ccx_admitdate));
      } else if (data.SortType === "Discharge") {
        if (a.ccx_dischargedate) {
          if (!b.ccx_dischargedate) {
            return 1;
          } else {
            aDate = moment(getFieldDate(a.ccx_dischargedate));
            bDate = moment(getFieldDate(b.ccx_dischargedate));
          }
        } else {
          if (b.ccx_dischargedate) {
            return -1;
          } else {
            return a["con.fullname"].localeCompare(b["con.fullname"]);
          }
        }
      }

      if (aDate.isBefore(bDate)) return 1;
      if (aDate.isAfter(bDate)) return -1;
      return 0;
    });

    const grouped = groupBy(filtered, "con.contactid");
    return grouped;
  }, [caseload, data.SearchQuery, data.SelectedProgram, data.SortType]);

  const filteredCount = useMemo(() => {
    if (caseload.length === 0) {
      return [];
    }

    let filtered = caseload.filter(
      (c) => !c["con.sor_clientflags"]?.includes("billing")
    );

    if (data.SearchQuery) {
      filtered = filtered.filter(
        (program) =>
          program["con.fullname"]
            .toLowerCase()
            .includes(data.SearchQuery.toLowerCase()) ||
          (program["con.ccx_mpistr"] !== undefined &&
            program["con.ccx_mpistr"]
              .toLowerCase()
              .includes(data.SearchQuery.toLowerCase()))
      );
    }

    if (data.SelectedProgram) {
      filtered = filtered.filter(
        (program) =>
          getFieldText(program.ccx_programunit) === data.SelectedProgram ||
          getFieldName(program.ccx_company).includes(data.SelectedProgram)
      );
    }

    filtered.sort((a, b) => {
      if (data.SortType === "A - Z") {
        return a["con.fullname"].localeCompare(b["con.fullname"]);
      }

      let aDate, bDate;

      if (data.SortType === "Admission") {
        aDate = moment(getFieldDate(a.ccx_admitdate));
        bDate = moment(getFieldDate(b.ccx_admitdate));
      } else if (data.SortType === "Discharge") {
        if (a.ccx_dischargedate) {
          if (!b.ccx_dischargedate) {
            return 1;
          } else {
            aDate = moment(getFieldDate(a.ccx_dischargedate));
            bDate = moment(getFieldDate(b.ccx_dischargedate));
          }
        } else {
          if (b.ccx_dischargedate) {
            return -1;
          } else {
            return a["con.fullname"].localeCompare(b["con.fullname"]);
          }
        }
      }

      if (aDate.isBefore(bDate)) return 1;
      if (aDate.isAfter(bDate)) return -1;
      return 0;
    });

    const grouped = groupBy(filtered, "con.contactid");
    return Object.keys(grouped).length;
  }, [caseload, data.SearchQuery, data.SelectedProgram, data.SortType]);

  const filteredProviderCaseload = useMemo(() => {
    if (providerCaseload.length === 0) {
      return [];
    }

    const records = providerCaseload.filter(
      (program) =>
        program["care.ccx_expdate"] === undefined &&
        program.ccx_dischargedate === undefined
    );

    let sorted = records.sort((a, b) =>
      a["con.fullname"].localeCompare(b["con.fullname"])
    );

    const grouped = groupBy(sorted, "con.contactid");
    return grouped;
  }, [providerCaseload]);

  const filteredProviders = useMemo(() => {
    if (!data.ProviderSearch) {
      return providers;
    }

    return providers.filter((provider) =>
      provider.name.toUpperCase().includes(data.ProviderSearch.toUpperCase())
    );
  }, [providers, data.ProviderSearch]);

  const handleClientSearch = async () => {
    setLoadStatus((ls) => {
      return { ...ls, loadingContacts: true, contactsLoaded: false };
    });
    const response = await searchContact(
      data.ClientSearch,
      safyInfo.user.safyInfo.BusinessUnitIdName,
      controllerSignal
    );

    if (controllerSignal.aborted) {
      return;
    }

    console.log(
      "ALL CLIENTS",
      response?.filter(
        (client) => getFieldName(client.ccx_contacttype) === "Client"
      ) || []
    );

    setClientSearchResults(
      response?.filter(
        (client) => getFieldName(client.ccx_contacttype) === "Client"
      ) || []
    );
    setLoadStatus((ls) => {
      return { ...ls, loadingContacts: false, contactsLoaded: true };
    });
  };

  const handkeKeyDown = (e) => {
    if (checkKeyPress(e)) {
      handleClientSearch();
    }
  };

  const filteredClientSearchResults = useMemo(() => {
    const filter = data.ClientSearch?.toLowerCase();

    return clientSearchResults.filter(
      (client) =>
        !filter ||
        client.fullname?.toLowerCase().includes(filter) ||
        `${client.firstname?.toLowerCase()} ${client.lastname?.toLowerCase()}`.includes(
          filter
        ) ||
        client.ccx_mpistr?.includes(filter)
    );
  }, [clientSearchResults, data.ClientSearch]);

  const handleFamilyChartClick = async (clientProgram) => {
    const response = await getCareTeam(clientProgram["con.contactid"]);
    if (isMountedRef.current) {
      if (response) {
        const collaterals = response.filter(
          (collateral) =>
            getFieldText(collateral.ccx_membertype) === "803080001" &&
            (!collateral.ccx_expdate ||
              moment(getFieldText(collateral.ccx_expdate)).isSameOrAfter(
                moment()
              ))
        );
        const children = collaterals.filter(
          (collateral) =>
            getFieldText(collateral.sor_contactrole) === "186460001"
        );
        children.sort((a, b) => {
          if (a.ccx_name > b.ccx_name) return 1;
          if (a.ccx_name < b.ccx_name) return -1;
          return 0;
        });
        const other = collaterals.filter(
          (collateral) =>
            getFieldText(collateral.sor_contactrole) === "186460009"
        );
        other.sort((a, b) => {
          if (a.ccx_name > b.ccx_name) return 1;
          else if (a.ccx_name < b.ccx_name) return -1;
          return 0;
        });

        setFamilyChart({
          clientName: clientProgram["con.fullname"],
          children,
          other,
        });
      }
    }
  };

  //Render UI
  return (
    <>
      {!loadingInProgress && stateDivisions ? (
        <>
          <div className="row">
            <div className="col-6">
              <div className="h5">Search By</div>
              <div className="btn-group border">
                <button
                  className={
                    data.CaseloadType === "Program Unit"
                      ? "btn btn-primary"
                      : "btn btn-light"
                  }
                  onClick={() =>
                    setData((d) => {
                      return { ...d, CaseloadType: "Program Unit" };
                    })
                  }
                >
                  Program Unit
                </button>
                <button
                  className={
                    data.CaseloadType === "Provider"
                      ? "btn btn-primary"
                      : "btn btn-light"
                  }
                  onClick={() =>
                    setData((d) => {
                      return { ...d, CaseloadType: "Provider" };
                    })
                  }
                >
                  Provider
                </button>
                <button
                  className={
                    data.CaseloadType === "Client"
                      ? "btn btn-primary"
                      : "btn btn-light"
                  }
                  onClick={() =>
                    setData((d) => {
                      return { ...d, CaseloadType: "Client" };
                    })
                  }
                >
                  Client
                </button>
              </div>
            </div>
            <div className="col">
              {data.CaseloadType === "Program Unit" && data.SelectedProgram ? (
                <div className="clearfix">
                  <div className="float-start form-check form-switch mt-3 pt-3">
                    <input
                      name="ShowDischarged"
                      className="form-check-input"
                      type="checkbox"
                      id="discharged"
                      checked={data.ShowDischarged}
                      onChange={handleShowDischargeChange}
                    />
                    <label className="form-check-label" htmlFor="discharged">
                      Show Discharged
                    </label>
                  </div>
                  <div className="float-end mt-5"></div>
                </div>
              ) : null}
            </div>
            <div className="col">
              {data.CaseloadType === "Program Unit" && data.SelectedProgram ? (
                <div className="float-end mt-3 pt-3">
                  <ToggleSwitch
                    values={["A - Z", "Admission", "Discharge"]}
                    selected={data.SortType}
                    onChange={(value) => {
                      setData((d) => {
                        const obj = { ...d };
                        obj.SortType = value;

                        if (value === "Discharge") {
                          obj.ShowDischarged = true;
                          loadCaseloadForSelectedProgram(
                            obj.SelectedProgram,
                            true
                          );
                        }

                        return obj;
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
          {data.CaseloadType === "Program Unit" ? (
            <>
              <div className="row mb-2 pt-3">
                <div className="col-6">
                  <Select
                    name="SelectedProgram"
                    label="Select Program Unit"
                    value={data.SelectedProgram}
                    options={programs}
                    onChange={handleSelectedProgramChange}
                  />
                </div>
                <div className="col-6">
                  <SearchBox
                    name="SearchQuery"
                    label="Search by Client Name"
                    value={data.SearchQuery}
                    onChange={handleSearchQueryChange}
                    clearSearch={() =>
                      setData((d) => {
                        return { ...d, SearchQuery: "" };
                      })
                    }
                    placeholder="Search by Client Name"
                  />
                </div>
              </div>
              {data.SelectedProgram ? (
                <div className="row">
                  <div className="col-8">
                    Program count:{" "}
                    <span className="bg-success text-white px-2 py-1 rounded">
                      <b>{filteredCount}</b>
                    </span>
                  </div>
                  <div className="col-2">
                    {safyInfo.user.modules.includes("Admin") === true ||
                    safyInfo.user.safyInfo.BusinessUnitIdName ===
                      "SAFY of Colorado" ||
                    safyInfo.user.safyInfo.BusinessUnitIdName ===
                      "SAFY of Nevada" ||
                    safyInfo.user.safyInfo.BusinessUnitIdName ===
                      "SAFY of Alabama" ? (
                      <Link id="tracking" title="Form Tracking" to="/tracking">
                        <div className={"btn btn-success float-end"}>
                          Outcomes Tracking
                        </div>
                      </Link>
                    ) : null}
                  </div>
                  <div className="col">
                    {data.SelectedProgram !== "" &&
                    !data.SelectedProgram.includes("SAFY") ? (
                      <div className="float-end">
                        {loadStatus.encountersLoaded ? (
                          <Link
                            className="w-100"
                            id="UnsignedProgramEncounters"
                            title="Open Unsigned Encounters"
                            to={`/unsignedprogramencounters/${data.SelectedProgram}`}
                          >
                            {encounters.length > 0 ? (
                              <div className="btn btn-danger">{`Review Encounters - ${encounters.length}`}</div>
                            ) : (
                              <div className="btn btn-success">{`Review Encounters`}</div>
                            )}
                          </Link>
                        ) : (
                          <div className="bg-warning p-2">
                            Loading Encounters
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className="row row-cols-1 row-cols-lg-2">
                {map(filteredCaseload, (client) => (
                  <ClientCard
                    key={client[0]["con.contactid"]}
                    client={client}
                    user={props.user}
                    careplans={
                      careplans &&
                      careplans.filter(
                        (cp) =>
                          getFieldText(cp["cp.ccx_patient"]) ===
                          client[0]["con.contactid"]
                      )
                    }
                    history={
                      formHistory &&
                      formHistory.filter((h) =>
                        client[0]["con.contactid"].includes(h.ContactId)
                      )
                    }
                    selectedProgram={data.SelectedProgram}
                    onFamilyChartClick={handleFamilyChartClick}
                  />
                ))}
              </div>
            </>
          ) : null}
          {data.CaseloadType === "Provider" ? (
            <>
              <div className="row mt-4">
                <div className="col">
                  <SearchBox
                    name="ProviderSearch"
                    label="Search by Provider Name"
                    value={data.ProviderSearch}
                    onChange={handleChange}
                    clearSearch={() =>
                      setData((d) => {
                        return { ...d, ProviderSearch: "" };
                      })
                    }
                    placeholder="Search by Provider Name"
                  />
                </div>
                <div className="col">
                  <Select
                    name="Provider"
                    label="Select a Provider"
                    value={data.Provider}
                    onChange={handleProviderChange}
                    options={filteredProviders}
                  ></Select>
                </div>
              </div>
              {Object.keys(filteredProviderCaseload).length > 0 ? (
                <>
                  <div className="row">
                    <div className="col-3">
                      Caseload Size:{" "}
                      <span className="bg-success text-white px-2 py-1 rounded">
                        <b>{Object.keys(filteredProviderCaseload).length}</b>
                      </span>
                    </div>
                  </div>
                  <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2">
                    {map(filteredProviderCaseload, (client) => (
                      <ClientCard
                        key={client[0]["con.contactid"]}
                        client={client}
                        user={safyInfo.user}
                        careplans={
                          careplans &&
                          careplans.filter(
                            (cp) =>
                              getFieldText(cp["cp.ccx_patient"]) ===
                              client[0]["con.contactid"]
                          )
                        }
                        history={
                          formHistory &&
                          formHistory.filter((h) =>
                            client[0]["con.contactid"].includes(h.ContactId)
                          )
                        }
                        onFamilyChartClick={handleFamilyChartClick}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : null}
          {data.CaseloadType === "Client" ? (
            <>
              <div className="row mt-4">
                <div className="col-4">
                  {/* <Select
                    name="clientSearchState"
                    label="Select State"
                    value={data.ClientSearchState}
                    options={[]}
                    onChange={() =>
                      setData((d) => {
                        return { ...d, ClientSearchState: "" };
                      })
                    }
                  /> */}
                  <SearchBox
                    name="ClientSearch"
                    label="Search by Client Name or MPI"
                    value={data.ClientSearch}
                    onChange={handleChange}
                    clearSearch={() =>
                      setData((d) => {
                        return { ...d, ClientSearch: "" };
                      })
                    }
                    placeholder="Search by Client Name or MPI"
                    onKeyDown={handkeKeyDown}
                  />
                </div>
                <div className="col-2">
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: "30px" }}
                    onClick={handleClientSearch}
                    disabled={!data.ClientSearch}
                  >
                    Search
                  </button>
                </div>
              </div>
              <div className="row my-2">
                <div className="col-3">
                  Caseload Size:{" "}
                  <span className="bg-success text-white px-2 py-1 rounded">
                    <b>{clientSearchResults.length}</b>
                  </span>
                </div>
              </div>
              <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2">
                {filteredClientSearchResults.map((client) => (
                  <div className="col">
                    <ClientMiniCard key={client.contactid} client={client} />
                  </div>
                ))}
              </div>
              <div className="mt-4 text-primary fw-bold">
                Or search including Non Program contacts
              </div>
              <AllCaseloadClientSearch />
            </>
          ) : null}
          {familyChart ? (
            <FamilyChartModal
              familyChart={familyChart}
              onCloseClick={() => setFamilyChart(undefined)}
            />
          ) : null}
        </>
      ) : (
        <div className="d-flex justify-content-center align-items-center">
          <h1>LOADING Team Caseload</h1>
          <Lottie
            className="w-25"
            height={120}
            width={120}
            loop={true}
            autoplay={true}
            animationData={fileloading.default}
            rendererSettings={{
              preserveAspectRatio: "xMidYMid slice",
            }}
          />
        </div>
      )}
    </>
  );
}

export default AllCaseloads;
